/**
 * Analytics Service
 * Central service for managing analytics tracking
 */

import type { AnalyticsProvider } from '../providers/base.provider'
import type { AnalyticsEventProperties } from '../types/properties'

interface AnalyticsContext {
  userId?: string
  contactId?: string
  locationId?: string
  groupSlug?: string
  groupId?: string
  pagePath?: string
  routeName?: string
  slugifiedRoute?: string
  email?: string
  appName?: string
  [key: string]: unknown
}

class AnalyticsService {
  private context: AnalyticsContext = {}
  private providers: Map<string, AnalyticsProvider> = new Map()
  private debug = false

  initialize(debug = false): void {
    this.debug = debug
    if (this.debug) console.log('[Analytics] Service initialized')
  }

  registerProvider(name: string, provider: AnalyticsProvider): void {
    this.providers.set(name, provider)
    if (this.debug) console.log(`[Analytics] Provider registered: ${name}`)
  }

  getProvider(name: string): AnalyticsProvider | undefined {
    return this.providers.get(name)
  }

  setContext(context: Partial<AnalyticsContext>): void {
    this.context = { ...this.context, ...context }
    if (this.debug) console.log('[Analytics] Context updated:', this.context)
  }

  updateContext(key: string, value: unknown): void {
    this.context[key] = value
  }

  getContext(): AnalyticsContext {
    return { ...this.context }
  }

  clearContext(): void {
    this.context = {}
  }

  private enrichProperties(properties?: AnalyticsEventProperties): AnalyticsEventProperties {
    return {
      ...this.context,
      ...properties,
      timestamp: Date.now(),
    }
  }

  track(event: string, properties?: AnalyticsEventProperties): void {
    const enriched = this.enrichProperties(properties)

    if (this.debug) console.log(`[Analytics] Track: ${event}`, enriched)

    this.providers.forEach((provider, name) => {
      try {
        provider.track(event, enriched)
      } catch (error) {
        console.error(`[Analytics] Provider ${name} error:`, error)
      }
    })
  }

  /** Sends an allow-listed payload without inheriting shared page or identity context. */
  trackProviderIsolated(name: string, event: string, properties: AnalyticsEventProperties): void {
    const provider = this.providers.get(name)
    if (!provider) return
    try {
      provider.track(event, { ...properties, timestamp: Date.now() })
    } catch (error) {
      console.error(`[Analytics] Provider ${name} error:`, error)
    }
  }

  /** @param contactId CRM contact id — sole user identity for GA (legacy parity). */
  identify(contactId: string, traits?: Record<string, unknown>): void {
    const identityContext: Partial<AnalyticsContext> = {}
    if (typeof traits?.locationId === 'string') {
      identityContext.locationId = traits.locationId
      identityContext.contactId = contactId
    }
    if (typeof traits?.appName === 'string') identityContext.appName = traits.appName
    this.setContext(identityContext)

    // Never log the contactId itself (CRM identity) — log only that identification occurred.
    if (this.debug) console.log('[Analytics] Identify: user identified', { appName: traits?.appName })

    this.providers.forEach((provider, name) => {
      try {
        provider.identify(contactId, traits)
      } catch (error) {
        console.error(`[Analytics] Provider ${name} identify error:`, error)
      }
    })
  }
}

export const analyticsService = new AnalyticsService()
